import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import type { ChartOptions, Plugin } from 'chart.js';
import {
  categoryTickFont,
  formatChartTick,
  hiddenAxisBorder,
  measureTextWidth,
  portalTooltipOptions,
  valueGrid,
} from './chartOptions';
import { chartFontFamily, chartLabelColor, resolveThemeColor } from './chartTheme';
import styles from './chartCanvas.module.css';

export interface HorizontalBarItem {
  key: string;
  label: string;
  value: number;
  color?: string;
  caption?: string;
}

interface HorizontalBarChartProps {
  items: readonly HorizontalBarItem[];
  ariaLabel: string;
  color?: string;
  height?: number;
  /** When set, the value axis is fixed (e.g. 100 for percentage charts). */
  maxValue?: number;
  /** Formats the value axis as percent ticks (0% … 100%). */
  percentScale?: boolean;
  formatTooltip?: (item: HorizontalBarItem) => string;
}

const CAPTION_FONT_SIZE = 12;
const ROW_HEIGHT = 44;
const CHART_PADDING = 36;
const MIN_HEIGHT = 96;
const MAX_HEIGHT = 360;

const HorizontalBarChart = ({
  items,
  ariaLabel,
  color = 'var(--color-brand-secondary)',
  height,
  maxValue,
  percentScale = false,
  formatTooltip,
}: HorizontalBarChartProps) => {
  const signature = items
    .map((item) => [
      item.key,
      item.label,
      item.value,
      item.caption ?? '',
      item.color ?? color,
    ].join('\u0000'))
    .join('\u0001');
  const resolvedHeight = height ?? contentBasedHeight(items.length);
  const axisMax = maxValue ?? (percentScale ? 100 : undefined);

  const chart = useMemo(() => {
    const font = `600 ${CAPTION_FONT_SIZE}px ${chartFontFamily()}`;
    const captions = items.map((item) => item.caption ?? '');
    const captionWidth = captions.reduce(
      (max, caption) => Math.max(max, measureTextWidth(caption, font)),
      0,
    );
    const labelColor = chartLabelColor();
    const data = {
      labels: items.map((item) => item.label),
      datasets: [
        {
          data: items.map((item) => (
            axisMax == null ? item.value : Math.min(item.value, axisMax)
          )),
          backgroundColor: items.map((item) => resolveThemeColor(item.color ?? color)),
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 18,
          categoryPercentage: 0.72,
          barPercentage: 0.9,
        },
      ],
    };
    const options: ChartOptions<'bar'> = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      locale: 'pt-BR',
      layout: {
        padding: {
          top: 4,
          right: captionWidth > 0 ? Math.ceil(captionWidth) + 14 : 8,
          bottom: 4,
          left: 0,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...portalTooltipOptions(),
          displayColors: false,
          callbacks: {
            label(context) {
              const item = items[context.dataIndex];
              if (!item) {
                return '';
              }
              return formatTooltip
                ? formatTooltip(item)
                : (item.caption ?? formatChartTick(item.value));
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ...(axisMax == null ? {} : { max: axisMax, suggestedMax: axisMax }),
          grid: valueGrid(),
          border: hiddenAxisBorder(),
          ticks: {
            ...categoryTickFont(11),
            maxTicksLimit: percentScale ? 5 : 5,
            ...(percentScale ? { stepSize: 25 } : { precision: 0 }),
            callback: (value) => {
              const numeric = typeof value === 'number' ? value : Number(value);
              if (!Number.isFinite(numeric)) {
                return '';
              }
              if (percentScale) {
                return `${formatChartTick(numeric)}%`;
              }
              return Number.isInteger(numeric) ? formatChartTick(numeric) : '';
            },
          },
        },
        y: {
          grid: { display: false },
          border: hiddenAxisBorder(),
          ticks: {
            autoSkip: false,
            color: labelColor,
            font: { family: chartFontFamily(), size: 13 },
          },
        },
      },
    };
    const plugins: Plugin<'bar'>[] = captionWidth === 0
      ? []
      : [createCaptionPlugin(captions, font, labelColor)];

    return { data, options, plugins };
  }, [axisMax, color, formatTooltip, items, percentScale, signature]);

  return (
    <div className={styles.frame} style={{ height: resolvedHeight }}>
      <Bar
        aria-label={ariaLabel}
        role="img"
        data={chart.data}
        options={chart.options}
        plugins={chart.plugins}
        fallbackContent={<span>{ariaLabel}</span>}
      />
    </div>
  );
};

function contentBasedHeight(count: number): number {
  if (count <= 0) {
    return MIN_HEIGHT;
  }

  return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, count * ROW_HEIGHT + CHART_PADDING));
}

function createCaptionPlugin(
  captions: readonly string[],
  font: string,
  color: string,
): Plugin<'bar'> {
  return {
    id: 'portalHorizontalBarCaption',
    afterDraw(chart) {
      const meta = chart.getDatasetMeta(0);
      const { ctx } = chart;
      ctx.save();
      ctx.font = font;
      ctx.fillStyle = color;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      meta.data.forEach((element, index) => {
        const caption = captions[index];
        if (!caption) {
          return;
        }
        const bar = element as unknown as { x: number; y: number; base?: number; horizontal?: boolean };
        const base = bar.base ?? bar.x;
        const endX = bar.horizontal === false ? bar.x : Math.max(bar.x, base);
        ctx.fillText(caption, endX + 8, bar.y);
      });
      ctx.restore();
    },
  };
}

export default HorizontalBarChart;
